import React, { useState } from 'react'

import FileCopy from '@mui/icons-material/FileCopy'
import {
  Button,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@mui/material'
import ApiLimitsTable from 'features/customers/components/api/ApiLimitsTable'
import { DOMAIN_ALLOWLIST_DESCRIPTION, ENRICH_METERS } from 'features/customers/constants/apiMeters'
import { useCustomer } from 'features/customers/hooks/useCustomer'
import { useCustomerEnrichApiKey } from 'features/customers/hooks/useCustomerEnrichApiKey'
import { useCustomerEnrichApiQuota } from 'features/customers/hooks/useCustomerEnrichApiQuota'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'

import LoadingView from 'components/LoadingView'

import DeleteEnrichApiKeyModal from './DeleteEnrichApiKeyModal'
import EnrichApiLimitsModal from './EnrichApiLimitsModal'
import GenerateEnrichApiKeyModal from './GenerateEnrichApiKeyModal'

const MODALS = {
  manageLimits: 'manageLimits',
  generateNewApiKey: 'generateNewApiKey',
  deleteApiKey: 'deleteApiKey',
}

type ApiKeyInputProps = {
  label: string
  value: string
}

const ApiKeyInput = ({ label, value }: ApiKeyInputProps) => (
  <div style={{ marginBottom: 24 }}>
    <Typography style={{ marginBottom: 8 }} variant="subtitle1">
      {label}
    </Typography>
    <OutlinedInput
      disabled
      endAdornment={
        <InputAdornment position="start">
          <Tooltip arrow placement="top" title="Copy to clipboard">
            <IconButton aria-label="copy" onClick={() => navigator.clipboard.writeText(value)} size="large">
              <FileCopy />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      }
      style={{ width: 455 }}
      value={value}
    />
  </div>
)

const EnrichApi = () => {
  const [modal, setModal] = useState<any>(null)
  const customerId = useCustomerId()
  const { data: customer } = useCustomer(customerId)
  const { data: apiKeys, isLoading: isApiKeyLoading, isError: isApiKeyError } = useCustomerEnrichApiKey()
  const hasApiKey = !!apiKeys
  const { data: { quota = 0 } = {}, isLoading: isQuotaLoading, isError } = useCustomerEnrichApiQuota(hasApiKey)
  const handleModalClose = () => setModal(null)

  const { publicApiKey, secretApiKey, publicApiKeyDomainAllowlist } = apiKeys || {}

  const enrichApiLimits = [
    {
      name: `${ENRICH_METERS.requests.label} limit`,
      description: ENRICH_METERS.requests.description,
      value: quota,
    },
    {
      name: 'Domain Allowlist',
      description: DOMAIN_ALLOWLIST_DESCRIPTION,
      value: publicApiKeyDomainAllowlist?.join(', ') || 'Not set',
    },
  ]

  const limitsActions = (
    <Button
      onClick={() => {
        setModal(MODALS.manageLimits)
      }}>
      Manage
    </Button>
  )

  const apiKeyActions = !(isQuotaLoading && isApiKeyLoading) && (
    <>
      {hasApiKey && (
        <Button
          onClick={() => {
            setModal(MODALS.deleteApiKey)
          }}>
          Delete API key
        </Button>
      )}
      <Button
        onClick={() => {
          setModal(MODALS.generateNewApiKey)
        }}>
        Generate new key
      </Button>
    </>
  )

  const keysSection = () => {
    if (isApiKeyError) return 'Error while loading Enrich API data'
    if (!hasApiKey) return 'No API key generated for this customer.'
    return (
      <>
        {publicApiKey && <ApiKeyInput label="Public Key" value={publicApiKey} />}
        {secretApiKey && <ApiKeyInput label="Private Key" value={secretApiKey} />}
      </>
    )
  }

  return (
    <div>
      <LoadingView loading={isQuotaLoading || isApiKeyLoading}>
        <CardHeader action={apiKeyActions} title="Enrich API" />
        <CardContent>{keysSection()}</CardContent>
      </LoadingView>
      {hasApiKey && (
        <>
          <CardHeader action={limitsActions} title="Limits" />
          <CardContent>
            {!isError ? <ApiLimitsTable rows={enrichApiLimits} /> : 'Error while loading Enrich API limits data'}
          </CardContent>
        </>
      )}
      {modal === MODALS.deleteApiKey && <DeleteEnrichApiKeyModal onClose={handleModalClose} />}
      {modal === MODALS.manageLimits && (
        <EnrichApiLimitsModal
          initialValues={{
            quota,
            publicApiKeyDomainAllowlist: publicApiKeyDomainAllowlist?.join(', ') || '',
          }}
          onClose={handleModalClose}
        />
      )}
      {modal === MODALS.generateNewApiKey && (
        <GenerateEnrichApiKeyModal
          initialValues={{
            quota: quota || 1000,
            hasKey: hasApiKey,
            publicApiKeyDomainAllowlist: publicApiKeyDomainAllowlist?.join(', ') || customer?.website || '',
          }}
          onClose={handleModalClose}
        />
      )}
    </div>
  )
}

export default EnrichApi
